import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const orderNumber = params.get("order");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | timeout
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      if (attempts > 10) { setStatus("timeout"); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("failed"); return; }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-xl mx-auto px-5 sm:px-8 py-24 text-center">
      {status === "checking" && (
        <>
          <Loader2 size={44} className="animate-spin text-[#306D29] mx-auto mb-6" />
          <h1 className="font-display text-3xl mb-2">Confirming your payment…</h1>
          <p className="text-[#5C5C5C]">Please wait a moment.</p>
        </>
      )}
      {status === "paid" && (
        <div data-testid="payment-success">
          <CheckCircle2 size={56} className="text-[#2D7A5D] mx-auto mb-6" />
          <h1 className="font-display text-4xl mb-3">Payment received!</h1>
          <p className="text-[#5C5C5C] mb-2">Thank you. We're now sourcing your order from the UK.</p>
          {orderNumber && (
            <p className="mb-8">Your order number is <span className="font-bold text-[#306D29]" data-testid="order-number">{orderNumber}</span></p>
          )}
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to={`/track?order=${orderNumber}`} className="inline-flex items-center gap-2 bg-[#306D29] text-white font-semibold px-7 py-3.5 rounded-full">
              Track order <ArrowRight size={18} />
            </Link>
            <Link to="/shop" className="inline-flex items-center gap-2 border border-black/10 font-semibold px-7 py-3.5 rounded-full">Continue shopping</Link>
          </div>
        </div>
      )}
      {(status === "failed" || status === "timeout") && (
        <div data-testid="payment-failed">
          <XCircle size={56} className="text-[#306D29] mx-auto mb-6" />
          <h1 className="font-display text-3xl mb-3">{status === "timeout" ? "Still processing" : "Payment not completed"}</h1>
          <p className="text-[#5C5C5C] mb-8">
            {status === "timeout"
              ? "Your payment is taking longer than usual. Check your order status shortly."
              : "Something went wrong with your payment. Please try again."}
          </p>
          <Link to="/cart" className="inline-flex items-center gap-2 bg-[#306D29] text-white font-semibold px-7 py-3.5 rounded-full">Back to cart</Link>
        </div>
      )}
    </div>
  );
}
